import { z } from 'zod';
import type { AtividadeEmpresa } from './atividades.js';
import type { EscopoToken } from './auth.js';
import type { AcessoDto, Papel, Permissao } from './permissoes.js';

/** GET /me/empresas: empresas que o usuario pode operar (alimenta o seletor; a escolhida vai em X-Empresa-Id). */
export interface EmpresaVisivel {
  id: string;
  nome: string;
  grupo: { id: string; nome: string };
  /** Papeis do usuario NESTA empresa (acessos de tenant, de grupo e da propria empresa). Alimenta o seletor de empresa do menu. */
  papeis?: Papel[];
  /** Sessao 24: as acoes do Copiloto estao ligadas nesta empresa (vale so se a env COPILOTO_ACOES=on; ver GET /copiloto/status). */
  acoesCopiloto: boolean;
  /** Sessao 24: perfil de atividade da empresa (so exibicao: tipos de item e menu). */
  atividades: AtividadeEmpresa[];
  /** Sessao 37: rotas do menu desligadas para a empresa (so exibicao); vazio = tudo ligado. */
  menusDesligados: string[];
  /** Sessao 24: empresa DEMO (faixa "DEMO" no topo). */
  demo: boolean;
}

/**
 * GET /me: usuario autenticado, seu tenant e as permissoes efetivas (alimenta nome, menu e botoes do web).
 * `permissoes` e `papeis` valem para a empresa do cabecalho X-Empresa-Id (ou a unica visivel); sem empresa
 * resolvida (varias visiveis e nenhuma escolhida) sao a uniao de todos os acessos e `empresaId` e nulo.
 * `acessos` lista os papeis e onde valem. So a API nega; o web apenas esconde.
 */
export interface MeDto {
  id: string;
  nome: string;
  email: string;
  tenant: { id: string; nome: string };
  empresaId: string | null;
  papeis: Papel[];
  permissoes: Permissao[];
  acessos: AcessoDto[];
  /** Escopo do token desta sessao. `TROCA_SENHA`: papeis, permissoes e acessos vem vazios ate a troca. */
  escopo: EscopoToken;
  deveTrocarSenha: boolean;
  /** Tem foto (GET /me/foto); `fotoVersao` muda a cada troca (cache do navegador). */
  temFoto: boolean;
  fotoVersao: string | null;
}

/** PATCH /me: o proprio usuario altera o nome. */
export const atualizarPerfilSchema = z.object({ nome: z.string().trim().min(2, 'Informe o nome (mínimo 2 letras)').max(120, 'O nome tem no máximo 120 caracteres') });
export type AtualizarPerfilDto = z.infer<typeof atualizarPerfilSchema>;

/** Foto do usuario: PNG ou JPEG de ate 256 KB (o web reduz antes de enviar). */
export const FOTO_USUARIO_MAX_BYTES = 256 * 1024;

/** GET/PUT /me/notificacoes-email: o usuario escolhe receber as notificacoes tambem por e-mail (padrao nao). `smtpConfigurado` = o servidor envia e-mail. */
export const preferenciaEmailSchema = z.object({ email: z.boolean() });
export type PreferenciaEmailDto = z.infer<typeof preferenciaEmailSchema>;
export interface PreferenciaEmailResponse {
  email: boolean;
  smtpConfigurado: boolean;
}

/** Sessao 32: empresa padrao do usuario, gravada NO SERVIDOR (vale em qualquer navegador). */
export interface EmpresaPadraoResponse {
  /** A ultima empresa escolhida, se o usuario ainda tem acesso e ela esta ativa; senao a primeira a que tem acesso (e grava essa); null so se nao tem nenhuma. */
  empresaId: string | null;
}
export const definirEmpresaAtualSchema = z.object({ empresaId: z.string().min(1) });
export type DefinirEmpresaAtualDto = z.infer<typeof definirEmpresaAtualSchema>;
